/**
 * Sanitizes a Genesis criteria string before sending to the server.
 * Strips empty parens, operator-only groups like (&& ), ( &&, and leading/trailing && to avoid
 * Groovy parse errors (e.g. MultipleCompilationErrorsException from invalid "(&& ) && (...)").
 */
export function sanitizeCriteriaString(criteria: string): string {
  let s = (criteria ?? '')
    .replace(/\s*\{\s*\}\s*/g, ' ')
    .replace(/\s*\(\s*\)\s*/g, ' ')
    .replace(/\s*\(\s*(&&|\|\|)\s*\)\s*/g, ' ');
  const hadLeadingOrphan = /^(\s*\(\s*)+(&&|\|\|)\s*/.test(s);
  s = s.replace(/^(\s*\(\s*)+(&&|\|\|)\s*/g, '');
  if (hadLeadingOrphan) {
    s = s.replace(/\)\)\)(\s*&&\s*)/g, ')$1');
  }
  s = s
    .replace(/(&&|\|\|)\s*(&&|\|\|)/g, '$1')
    .replace(/\(\s*(&&|\|\|)/g, '(')
    .replace(/(&&|\|\|)\s*\)/g, ')')
    .replace(/\s*\(\s*\)\s*/g, ' ');
  let result = s
    .replace(/^(\s*&&\s*)+/, '')
    .replace(/\s*&&\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  result = balanceParenthesesInCriteria(result);
  result = balanceBracesInCriteria(result);
  return result;
}

function reduceCriteriaWithQuoteAwareDelimiters(
  criteria: string,
  options: { openChar: '(' | '{'; closeChar: ')' | '}'; keepOpenChar: boolean; keepCloseChar: boolean }
): string {
  const input = criteria ?? '';
  const out: string[] = [];
  let depth = 0;
  let inQuote: string | null = null;

  for (let i = 0; i < input.length; i++) {
    const c = input[i];
    if (inQuote) {
      out.push(c);
      if (c === '\\' && i + 1 < input.length) {
        out.push(input[++i]);
      } else if (c === inQuote) {
        inQuote = null;
      }
      continue;
    }
    if (c === '"' || c === "'") {
      inQuote = c;
      out.push(c);
      continue;
    }

    if (c === options.openChar) {
      depth++;
      if (options.keepOpenChar) out.push(c);
      continue;
    }

    if (c === options.closeChar) {
      if (depth > 0) {
        depth--;
        if (options.keepCloseChar) out.push(c);
      }
      continue;
    }

    out.push(c);
  }

  if (depth > 0 && options.keepOpenChar && options.keepCloseChar) {
    out.push(options.closeChar.repeat(depth));
  }

  return out.join('');
}

/**
 * Drops extra closing parens (e.g. after BOND_ID multi-value replace + foundation "((...))" wrappers)
 * so Groovy criteria does not hit Unexpected input: ')'.
 */
export function balanceParenthesesInCriteria(criteria: string): string {
  return reduceCriteriaWithQuoteAwareDelimiters(criteria, {
    openChar: '(',
    closeChar: ')',
    keepOpenChar: true,
    keepCloseChar: true,
  });
}

/**
 * Drops stray braces that may be emitted by UI filter builders (e.g. "{}"),
 * which are invalid for Genesis criteria grammar.
 */
export function balanceBracesInCriteria(criteria: string): string {
  return reduceCriteriaWithQuoteAwareDelimiters(criteria, {
    openChar: '{',
    closeChar: '}',
    keepOpenChar: false,
    keepCloseChar: false,
  });
}

/** Removes DUE_TIME "due within next N ms" window from criteria (client-built). */
export function stripTimeTillDueWindowFromCriteria(criteria: string): string {
  let s = criteria ?? '';
  s = s.replace(/\(\(\s*DUE_TIME\s*>=\s*\d+\s*&&\s*DUE_TIME\s*<=\s*\d+\s*\)\)/g, '');
  s = s.replace(/\(\s*DUE_TIME\s*>=\s*\d+\s*&&\s*DUE_TIME\s*<=\s*\d+\s*\)/g, '');
  return s;
}

/** Reads DUE_TIME bounds from client-built `((DUE_TIME >= n && DUE_TIME <= m))` criteria, if present. */
export function parseDueTimeWindowMs(criteria: string): { start: number; end: number } | null {
  const raw = criteria ?? '';
  const numericMatch = /\bDUE_TIME\s*>=\s*(\d+)\s*&&\s*DUE_TIME\s*<=\s*(\d+)/.exec(raw);
  if (numericMatch) {
    const start = Number(numericMatch[1]);
    const end = Number(numericMatch[2]);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      return { start, end };
    }
  }
  const dateExprMatch =
    /Expr\.dateTimeIsGreaterEqual\(\s*DUE_TIME\s*,\s*["']([^"']+)["']\s*\)\s*&&\s*Expr\.dateTimeIsLessEqual\(\s*DUE_TIME\s*,\s*["']([^"']+)["']\s*\)/.exec(
      raw
    );
  if (!dateExprMatch) return null;
  const start = parseGenesisDateTimeLiteral(dateExprMatch[1]);
  const end = parseGenesisDateTimeLiteral(dateExprMatch[2]);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
}

function parseGenesisDateTimeLiteral(value: string): number {
  const m = /^(\d{4})(\d{2})(\d{2})-(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!m) return Number.NaN;
  const [, y, mo, d, h, mi, s] = m;
  return Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    s ? Number(s) : 0,
    0
  );
}

function toGenesisDateTimeLiteral(epochMs: number): string {
  const dt = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}-${pad(
    dt.getUTCHours()
  )}:${pad(
    dt.getUTCMinutes()
  )}`;
}

/** Builds an Expr datetime range clause for DUE_TIME using Genesis dateTime literal format. */
export function buildDueTimeDateTimeExprRange(startMs: number, endMs: number): string {
  const start = toGenesisDateTimeLiteral(startMs);
  const end = toGenesisDateTimeLiteral(endMs);
  return `((Expr.dateTimeIsGreaterEqual(DUE_TIME, "${start}") && Expr.dateTimeIsLessEqual(DUE_TIME, "${end}")))`;
}

/**
 * Removes criteria fragments for UI-only Due within fields (not on the server).
 * foundation-filters emits containsIgnoreCase / equals / EQ for typed text and enums.
 */
export function stripDueWithinFormFieldsFromCriteria(criteria: string): string {
  let s = criteria ?? '';
  const DUE_WITHIN_FIELDS = ['DUE_WITHIN_AMOUNT', 'DUE_WITHIN_UNIT'];
  const PARENS_CONTENT = '[^)]+';
  const QUOTED_LITERAL = `["'][^"']*["']`;
  const dueWithinContainsRegexes = DUE_WITHIN_FIELDS.map(
    (field) => new RegExp(`Expr\\.containsIgnoreCase\\s*\\(\\s*${field}\\s*,\\s*${PARENS_CONTENT}\\)`, 'g')
  );
  const dueWithinStringPredicateRegexes = DUE_WITHIN_FIELDS.flatMap((field) => [
    new RegExp(`${field}\\.equals\\(${PARENS_CONTENT}\\)`, 'g'),
    new RegExp(`${field}\\.startsWith\\(${PARENS_CONTENT}\\)`, 'g'),
    new RegExp(`${field}\\.endsWith\\(${PARENS_CONTENT}\\)`, 'g'),
  ]);
  const dueWithinEqualsRegexes = DUE_WITHIN_FIELDS.map(
    (field) => new RegExp(`${field}\\s*==\\s*${QUOTED_LITERAL}`, 'g')
  );
  const patterns: RegExp[] = [
    ...dueWithinContainsRegexes,
    ...dueWithinStringPredicateRegexes,
    ...dueWithinEqualsRegexes,
  ];
  for (const p of patterns) {
    s = s.replace(p, '');
  }
  return s;
}

/**
 * Removes UI-only bid-time form field fragments from criteria.
 * Covers legacy `FIRM_FOR_HOURS`, `FIRM_FOR_MIN` / `FIRM_FOR_MAX`, and `FIRM_FOR.min` / `FIRM_FOR.max` form fragments.
 * Does not strip server criteria `FIRM_FOR >= …` / `FIRM_FOR <= …` (no dot before the operator).
 */
export function stripBidTimeHoursFormFieldsFromCriteria(criteria: string): string {
  let s = criteria ?? '';
  const fragment =
    /(^|\s*&&?\s*)\(*\s*(?:FIRM_FOR_HOURS|FIRM_FOR_MIN|FIRM_FOR_MAX|FIRM_FOR\.min|FIRM_FOR\.max)\s*(==|>=|<=|>|<)\s*[^)&|]+\)*/g;
  let prev: string;
  do {
    prev = s;
    s = s.replace(fragment, '');
  } while (s !== prev);
  // String min/max fields often emit containsIgnoreCase(...) — remove so we only apply ms range from appendBidTimeMsCriteria.
  do {
    prev = s;
    s = s.replace(
      /\(*\s*Expr\.containsIgnoreCase\s*\(\s*FIRM_FOR\.(min|max)\s*,\s*[^)]+\)\s*\)+\)*/g,
      ''
    );
  } while (s !== prev);
  return s;
}

/**
 * Removes client-built `FIRM_FOR` (ms, view `MUNICIPAL_BOND_VIEW`) criteria clauses from a string.
 * Use before merging export bond rows — `ACTIVE_MUNICIPAL_BONDS_EXPORT` rows may not contain `FIRM_FOR`.
 */
export function stripBidTimeMsCriteriaFromCriteria(criteria: string): string {
  let s = criteria ?? '';
  s = s.replace(/\(\(\s*FIRM_FOR\s*>=\s*[\d.]+\s*&&\s*FIRM_FOR\s*<=\s*[\d.]+\s*\)\)/g, '');
  s = s.replace(/\(\(\s*FIRM_FOR\s*>=\s*[\d.]+\s*\)\)/g, '');
  s = s.replace(/\(\(\s*FIRM_FOR\s*<=\s*[\d.]+\s*\)\)/g, '');
  return s;
}

/**
 * Removes every client/foundation-generated predicate on column `FIRM_FOR` so we can replace it with
 * ms-range criteria. UI may emit hours or contains(); server LONG is ms.
 */
/** Removes upload-only `CORRELATION_ID == '…'` clauses (not valid on live export views). */
export function stripCorrelationIdFromCriteria(criteria: string): string {
  let s = (criteria ?? '').replace(
    /(^|\s*&&\s*)\(*\s*CORRELATION_ID\s*==\s*['"][^'"]+['"]\s*\)*/gi,
    ''
  );
  s = s.replace(/\s*&&\s*$/, '');
  return sanitizeCriteriaString(s);
}

/**
 * Removes Reference# / BOND_ID predicates so ReqRep can apply all other filters server-side.
 * Client still applies BOND_ID filtering after the response.
 */
export function stripBondIdFromCriteria(criteria: string): string {
  let s = criteria ?? '';

  // Expr.containsIgnoreCase(BOND_ID, "…")
  s = s.replace(/\(*\s*Expr\.containsIgnoreCase\s*\(\s*BOND_ID\s*,\s*[^)]+\)\s*\)+/gi, '');

  // (BOND_ID == 'a' || BOND_ID == 'b' || …)
  s = s.replace(
    /\(\s*BOND_ID\s*==\s*['"][^'"]+['"]\s*(\|\|\s*BOND_ID\s*==\s*['"][^'"]+['"]\s*)+\)/gi,
    ''
  );

  // Single BOND_ID == '…' (and optional wrapping parens)
  s = s.replace(/(^|\s*&&\s*)\(*\s*BOND_ID\s*==\s*['"][^'"]+['"]\s*\)*/gi, '');

  // Legacy string helpers sometimes emitted by foundation-filters
  s = s.replace(/\(*\s*BOND_ID\.(?:equals|startsWith|endsWith)\s*\([^)]*\)\s*\)*/gi, '');

  return sanitizeCriteriaString(s);
}

/**
 * Criteria sent to ACTIVE_MUNICIPAL_BONDS_EXPORT / CPTY_BOND_ACTIVITY_EXPORT.
 * Includes flyout/top-bar filters except Reference# (BOND_ID) and upload correlation.
 */
export function buildReqRepExportCriteria(filterValue: string): string {
  let criteria = filterValue ?? '';
  criteria = stripCorrelationIdFromCriteria(criteria);
  criteria = stripBondIdFromCriteria(criteria);
  criteria = stripDueWithinFormFieldsFromCriteria(criteria);
  criteria = stripBidTimeHoursFormFieldsFromCriteria(criteria);
  return sanitizeCriteriaString(criteria);
}

/**
 * Builds ReqRep DETAILS for export. Strips BOND_ID and CORRELATION_ID (and UI-only
 * form fields) so CRITERIA_MATCH matches what the grid would send minus Reference#
 * and upload correlation — those stay client-side only after the ReqRep response.
 */
export function buildReqRepExportDetails(filterValue: string): Record<string, unknown> {
  const trimmed = buildReqRepExportCriteria(filterValue ?? '');
  if (!trimmed) {
    return {};
  }
  return { CRITERIA_MATCH: trimmed };
}

export function stripFoundationBidTimeColumnCriteria(criteria: string): string {
  let s = stripBidTimeMsCriteriaFromCriteria(criteria ?? '');
  let prev: string;
  do {
    prev = s;
    s = s.replace(/\(*\s*Expr\.containsIgnoreCase\s*\(\s*FIRM_FOR\s*,\s*[^)]+\)\s*\)+/g, '');
    s = s.replace(
      /\(\s*FIRM_FOR\s*>=\s*[\d.]+\s*&&\s*FIRM_FOR\s*<=\s*[\d.]+\s*\)/g,
      ''
    );
    s = s.replace(/\bFIRM_FOR\s*>=\s*[\d.]+\s*&&\s*FIRM_FOR\s*<=\s*[\d.]+/g, '');
    s = s.replace(/\bFIRM_FOR\s*>=\s*[\d.]+/g, '');
    s = s.replace(/\bFIRM_FOR\s*<=\s*[\d.]+/g, '');
    s = s.replace(/\bFIRM_FOR\s*==\s*[\d.]+/g, '');
  } while (s !== prev);
  return sanitizeCriteriaString(s);
}
